import { FC, useState } from "react";

import { LiveFormEditorProvider } from "./common/LiveFormEditorContext";
import { useInitLiveFormFeatureFlags } from "./common/useInitLiveFormFeatureFlags";
import { DonationEmailLivePreview } from "./LivePreview/EmailPreview/DonationEmailLivePreview";
import { DonationLiveEditorStepAutomatedCommunications } from "./Steps/StepAutomatedCommunications/DonationLiveEditorStepAutomatedCommunications";
import { LiveEditorStepBusinessModel } from "./Steps/StepBusinessModel/LiveEditorStepBusinessModel";

import { DonationFormWithUserOrOrgAccessOutput } from "@/common/types/trpc";
import { useLogFormEditionStarted } from "@/features/LiveFormEditor/common/amplitude/useLogFormEditionStarted";
import { LiveFormEditorBase } from "@/features/LiveFormEditor/LiveFormEditorBase";
import { DonationFormLivePreview } from "@/features/LiveFormEditor/LivePreview/FormLivePreview/DonationFormLivePreview";
import { TaxReceiptLivePreview } from "@/features/LiveFormEditor/LivePreview/TaxReceiptLivePreview/TaxReceiptLivePreview";
import { useGetAllDonationFormStepsInOrder } from "@/features/LiveFormEditor/Navigation/useGetAllDonationFormStepsInOrder";
import { DonationLiveEditorStepAdvancedSettings } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/DonationLiveEditorStepAdvancedSettings";
import { DonationLiveEditorStepBanner } from "@/features/LiveFormEditor/Steps/StepBanner/DonationLiveEditorStepBanner";
import { DonationLiveEditorStepColor } from "@/features/LiveFormEditor/Steps/StepColor/DonationLiveEditorStepColor";
import { StepDescriptionSelector } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionSelector";
import { DonationLiveEditorStepFundraisingGoal } from "@/features/LiveFormEditor/Steps/StepFundraisingGoal/DonationLiveEditorStepFundraisingGoal";
import { DonationLiveEditorStepLogo } from "@/features/LiveFormEditor/Steps/StepLogo/DonationLiveEditorStepLogo";
import { LiveFormEditorP2PSettingsStep } from "@/features/LiveFormEditor/Steps/StepP2PSettings/LiveFormEditorP2PSettingsStep";
import { DonationLiveEditorStepQuestionsV4 } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/DonationLiveEditorStepQuestionsV4";
import { StepQuickBooksMapping } from "@/features/LiveFormEditor/Steps/StepQuickBooksMapping/StepQuickBooksMapping";
import { DonationLiveEditorStepReview } from "@/features/LiveFormEditor/Steps/StepReview/DonationLiveEditorStepReview";
import { DonationLiveEditorStepSuggestedDonationAmounts } from "@/features/LiveFormEditor/Steps/StepSuggestedDonationAmounts/DonationLiveEditorStepSuggestedDonationAmounts";
import { DonationLiveEditorStepTaxReceipt } from "@/features/LiveFormEditor/Steps/StepTaxReceipt/DonationLiveEditorStepTaxReceipt";
import { DonationLiveEditorStepTitle } from "@/features/LiveFormEditor/Steps/StepTitle/DonationLiveEditorStepTitle";
import { LiveDonationData, LiveFormEditorMode } from "@/features/LiveFormEditor/types/LiveFormEditor.types";

type DonationLiveFormEditorProps = {
  apiDonationForm: DonationFormWithUserOrOrgAccessOutput;
  editorMode?: LiveFormEditorMode;
  optionalFundraiserToken?: string;
  defaultExitUrl?: string;
};

export const DonationLiveFormEditor: FC<DonationLiveFormEditorProps> = ({
  apiDonationForm,
  editorMode = "org",
  optionalFundraiserToken,
  defaultExitUrl,
}) => {
  const [liveFormData, setLiveFormData] = useState<LiveDonationData | null>(null);
  const [apiFormObject, setApiFormObject] = useState<DonationFormWithUserOrOrgAccessOutput>(apiDonationForm);
  const [editOrCreateMode, setEditOrCreateMode] = useState<"edit" | "create">(
    apiDonationForm.status === "draft" ? "create" : "edit"
  );
  const { steps } = useGetAllDonationFormStepsInOrder({
    apiDonationForm,
    editOrCreateMode,
    editorMode,
  });

  useLogFormEditionStarted(apiDonationForm, "donation-form");

  const featureFlags = useInitLiveFormFeatureFlags("donation-form", apiFormObject);

  return (
    <LiveFormEditorProvider
      setApiFormObject={setApiFormObject}
      setLiveFormData={setLiveFormData}
      steps={steps}
      liveFormData={liveFormData}
      apiFormObject={apiFormObject}
      editOrCreateMode={editOrCreateMode}
      setEditOrCreateMode={setEditOrCreateMode}
      formType="donation-form"
      formCategory={apiDonationForm.category}
      editorMode={editorMode}
      optionalFundraiserToken={optionalFundraiserToken}
      defaultExitUrl={defaultExitUrl}
      featureFlags={featureFlags}
    >
      {({ currentStepKey, previewType }) => (
        <LiveFormEditorBase
          slots={{
            steps: (
              <>
                {currentStepKey === "title" && <DonationLiveEditorStepTitle />}
                {currentStepKey === "fundraising-goal" && <DonationLiveEditorStepFundraisingGoal />}
                {currentStepKey === "description" && <StepDescriptionSelector />}
                {currentStepKey === "color" && <DonationLiveEditorStepColor />}
                {currentStepKey === "banner" && <DonationLiveEditorStepBanner />}
                {currentStepKey === "logo" && <DonationLiveEditorStepLogo />}
                {currentStepKey === "suggested-donation-amounts" && <DonationLiveEditorStepSuggestedDonationAmounts />}
                {currentStepKey === "tax-receipts" && <DonationLiveEditorStepTaxReceipt />}
                {currentStepKey === "questions" && <DonationLiveEditorStepQuestionsV4 />}
                {currentStepKey === "business-model" && <LiveEditorStepBusinessModel />}
                {currentStepKey === "thank-you-email" && <DonationLiveEditorStepAutomatedCommunications />}
                {currentStepKey === "quickbooks-mapping" && <StepQuickBooksMapping />}
                {currentStepKey === "advanced-settings" && <DonationLiveEditorStepAdvancedSettings />}
                {currentStepKey === "review" && <DonationLiveEditorStepReview />}
                {currentStepKey === "p2p-settings" && <LiveFormEditorP2PSettingsStep />}
              </>
            ),
            preview: (
              <>
                {previewType === "form" && <DonationFormLivePreview />}
                {previewType === "email" && <DonationEmailLivePreview />}
                {previewType === "tax-receipt" && <TaxReceiptLivePreview />}
              </>
            ),
          }}
        />
      )}
    </LiveFormEditorProvider>
  );
};
